import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Settings, Plus, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const DEFAULT_SEVERITY_LABELS: Record<string, string> = {
  '1': 'Insignificant',
  '2': 'Minor',
  '3': 'Moderate',
  '4': 'Major',
  '5': 'Catastrophic',
};

const DEFAULT_PROBABILITY_LABELS: Record<string, string> = {
  '1': 'Rare',
  '2': 'Unlikely',
  '3': 'Possible',
  '4': 'Likely',
  '5': 'Almost Certain',
};

const DEFAULT_THRESHOLDS = { low: 10, medium: 50, high: 75 };

const AdminConfig = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [configId, setConfigId] = useState<string | null>(null);

  // Hazard categories
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState('');

  // Risk thresholds
  const [severityLabels, setSeverityLabels] = useState<Record<string, string>>({ ...DEFAULT_SEVERITY_LABELS });
  const [probabilityLabels, setProbabilityLabels] = useState<Record<string, string>>({ ...DEFAULT_PROBABILITY_LABELS });
  const [thresholds, setThresholds] = useState({ ...DEFAULT_THRESHOLDS });

  const { toast } = useToast();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_configuration')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setConfigId(data.id);

        // Parse hazard categories
        if (Array.isArray(data.hazard_categories)) {
          setCategories(data.hazard_categories as string[]);
        }

        // Parse risk thresholds
        if (data.risk_thresholds && typeof data.risk_thresholds === 'object') {
          const rt = data.risk_thresholds as Record<string, any>;
          if (rt.low != null) setThresholds({ low: rt.low, medium: rt.medium, high: rt.high });
          if (rt.severity_labels) setSeverityLabels(rt.severity_labels);
          if (rt.probability_labels) setProbabilityLabels(rt.probability_labels);
        }
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const addCategory = () => {
    const trimmed = newCategory.trim().toLowerCase().replace(/\s+/g, '_');
    if (!trimmed) return;
    if (categories.includes(trimmed)) {
      toast({ title: "Duplicate", description: "This category already exists", variant: "destructive" });
      return;
    }
    setCategories([...categories, trimmed]);
    setNewCategory('');
  };

  const removeCategory = (cat: string) => {
    setCategories(categories.filter(c => c !== cat));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const riskThresholdsPayload = {
        low: thresholds.low,
        medium: thresholds.medium,
        high: thresholds.high,
        severity_labels: severityLabels,
        probability_labels: probabilityLabels,
      };

      if (!configId) throw new Error('No configuration found');

      const { error } = await supabase
        .from('ai_configuration')
        .update({
          hazard_categories: categories,
          risk_thresholds: riskThresholdsPayload,
          updated_by: user.id,
        })
        .eq('id', configId);

      if (error) throw error;

      toast({ title: "Success", description: "Configuration saved successfully" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const formatLabel = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="space-y-6">
      {/* Hazard Categories */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <CardTitle>Custom Hazard Categories</CardTitle>
          </div>
          <CardDescription>
            Define the hazard categories used by AI to classify reported hazards. Add or remove categories to match your operations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Badge key={cat} variant="secondary" className="flex items-center gap-1.5 px-3 py-1.5 text-sm">
                {formatLabel(cat)}
                <button onClick={() => removeCategory(cat)} className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {categories.length === 0 && (
              <p className="text-sm text-muted-foreground italic">No categories configured. Add your first category below.</p>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="e.g. electrical_hazard"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
              className="max-w-xs"
            />
            <Button variant="outline" size="sm" onClick={addCategory} className="flex items-center gap-1">
              <Plus className="h-4 w-4" /> Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Risk Score Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Risk Score Configuration</CardTitle>
          <CardDescription>
            Define how severity and probability are labeled and set the thresholds for Low, Medium, and High risk classification.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Severity Labels */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Severity Levels</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {['1', '2', '3', '4', '5'].map((level) => (
                <div key={`sev-${level}`} className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground w-4">{level}</span>
                  <Input
                    value={severityLabels[level] || ''}
                    onChange={(e) => setSeverityLabels({ ...severityLabels, [level]: e.target.value })}
                    placeholder={DEFAULT_SEVERITY_LABELS[level]}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Probability Labels */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Probability Levels</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {['1', '2', '3', '4', '5'].map((level) => (
                <div key={`prob-${level}`} className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground w-4">{level}</span>
                  <Input
                    value={probabilityLabels[level] || ''}
                    onChange={(e) => setProbabilityLabels({ ...probabilityLabels, [level]: e.target.value })}
                    placeholder={DEFAULT_PROBABILITY_LABELS[level]}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Thresholds */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Risk Classification Thresholds</Label>
            <p className="text-sm text-muted-foreground">
              Scores from severity × probability are classified using these cutoffs (max score = 25).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label htmlFor="low" className="text-sm">Low (up to)</Label>
                <Input
                  id="low"
                  type="number"
                  value={thresholds.low}
                  onChange={(e) => setThresholds({ ...thresholds, low: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="medium" className="text-sm">Medium (up to)</Label>
                <Input
                  id="medium"
                  type="number"
                  value={thresholds.medium}
                  onChange={(e) => setThresholds({ ...thresholds, medium: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="high" className="text-sm">High (above)</Label>
                <Input
                  id="high"
                  type="number"
                  value={thresholds.high}
                  onChange={(e) => setThresholds({ ...thresholds, high: Number(e.target.value) })}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={isLoading}>
        {isLoading ? "Saving..." : "Save Configuration"}
      </Button>
    </div>
  );
};

export default AdminConfig;
